import axios from "axios";
import { debug, formatSafeErrorMessage } from "../../../utils/debugLogger";
import { TIKTOK_SCOPES, getTikTokClientKey, getTikTokRedirectUri } from "../config/tiktokEnv";

const TIKTOK_AUTH_BASE_URL = "https://www.tiktok.com/v2/auth/authorize/";

const SOCIAL_API_BASE =
  process.env.REACT_APP_SOCIAL_API_URL ||
  "https://bc631mqtg1.execute-api.us-east-1.amazonaws.com/Prod";

/**
 * Own axios instance, deliberately not the global one. The app-wide interceptor
 * signs the user out on any 401/403 — but a 403 here (the selected customer
 * briefly not matching the profile being queried, e.g. right after a brand
 * switch) or a transient 401 must not end the session. Same reasoning as
 * socialConnectionsService / ghlOutreach / the Facebook Graph client. Callers
 * pass the idToken from the session; a 401/403 is surfaced as a friendly error.
 */
const client = axios.create({ baseURL: SOCIAL_API_BASE, timeout: 15000 });

/** OAuth-state provider key; the callback validates against the same one. */
export const TIKTOK_OAUTH_STATE_PROVIDER = "tiktok";

export interface TikTokExchangeResponse {
  success: boolean;
  profile_id: string;
  display_name: string;
  scope: string;
}

export interface TikTokCreatorInfo {
  creator_nickname: string | null;
  creator_username: string | null;
  creator_avatar_url: string | null;
  privacy_level_options: string[];
  comment_disabled: boolean;
  duet_disabled: boolean;
  stitch_disabled: boolean;
  max_video_post_duration_sec: number | null;
}

/** The backend answered 409 reconnect_required: the stored TikTok grant is dead. */
export class TikTokReconnectRequiredError extends Error {
  constructor() {
    super("Your TikTok connection has expired. Please reconnect TikTok.");
    this.name = "TikTokReconnectRequiredError";
  }
}

/** The backend answered 404 profile_not_connected: that profile is no longer connected for this customer. */
export class TikTokNotConnectedError extends Error {
  constructor() {
    super("This TikTok account is no longer connected.");
    this.name = "TikTokNotConnectedError";
  }
}

const isProfileNotConnected = (error: unknown): boolean => {
  const err = error as { response?: { status?: number; data?: { error?: string } } };
  return err?.response?.status === 404 && err?.response?.data?.error === "profile_not_connected";
};

/**
 * Build the TikTok consent URL. `state` must come from createOAuthState("tiktok").
 * The client secret never appears here; the backend holds it.
 */
export function getTikTokAuthUrl(state: string): string {
  const clientKey = getTikTokClientKey();
  if (!clientKey) {
    throw new Error("TikTok client key is not configured");
  }
  const params = new URLSearchParams({
    client_key: clientKey,
    response_type: "code",
    scope: TIKTOK_SCOPES,
    redirect_uri: getTikTokRedirectUri(),
    state,
  });
  return `${TIKTOK_AUTH_BASE_URL}?${params.toString()}`;
}

/**
 * Turn a failed TikTok route call into a message a person can act on.
 *
 * The backend never answers 401/403 for TikTok-side problems, so 409/502/503
 * are the expected failures here and are mapped explicitly. A 401/403 means the
 * session or the selected workspace changed under the call; it is reported as a
 * message (these calls use their own axios client, so it never logs the user out).
 */
export function tiktokErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { status?: number; data?: { message?: string; error?: string } };
    message?: string;
  };
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 401 || status === 403) {
    return "Your session or workspace changed — refresh and try again.";
  }
  if (status === 409 || data?.error === "reconnect_required") {
    return "Your TikTok connection has expired. Please reconnect TikTok.";
  }
  if (status === 503 || data?.error === "tiktok_not_configured") {
    return "TikTok connection isn't available yet. Please try again later.";
  }
  if (status === 502) {
    return "TikTok didn't respond as expected. Please try again in a moment.";
  }
  const serverMessage = data?.message || data?.error;
  if (status === 400 && serverMessage && /video\.publish|permission/i.test(serverMessage)) {
    return (
      "TikTok didn't get permission to post for you. Connect again and allow the " +
      "posting permission when TikTok asks."
    );
  }
  return serverMessage || err?.message || fallback;
}

function requireArgs(customerId: string, idToken: string) {
  if (!customerId?.trim()) throw new Error("Customer ID is required for TikTok");
  if (!idToken?.trim()) throw new Error("User authentication token is required");
}

/** Exchange the authorization code server-side; no tokens come back to the browser. */
export async function exchangeTikTokCode(
  customerId: string,
  code: string,
  redirectUri: string,
  idToken: string
): Promise<TikTokExchangeResponse> {
  requireArgs(customerId, idToken);
  try {
    const response = await client.post(
      `/${customerId}/tiktok/exchange-token`,
      { code, redirect_uri: redirectUri },
      { headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error: unknown) {
    debug.error("TikTok token exchange failed:", formatSafeErrorMessage(error));
    throw new Error(tiktokErrorMessage(error, "Failed to connect TikTok. Please try again."));
  }
}

/** Live nickname/avatar for a connected profile. Throws TikTokReconnectRequiredError on 409. */
export async function getTikTokCreatorInfo(
  customerId: string,
  profileId: string,
  idToken: string
): Promise<TikTokCreatorInfo> {
  requireArgs(customerId, idToken);
  try {
    const response = await client.get(`/${customerId}/tiktok/creator-info`, {
      params: { profile_id: profileId },
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 409) throw new TikTokReconnectRequiredError();
    if (isProfileNotConnected(error)) throw new TikTokNotConnectedError();
    debug.error("TikTok creator-info failed:", formatSafeErrorMessage(error));
    throw new Error(tiktokErrorMessage(error, "Failed to load TikTok account details"));
  }
}

/** Revoke at TikTok and delete our stored token. Rejects on failure. */
export async function disconnectTikTok(
  customerId: string,
  profileId: string,
  idToken: string
): Promise<void> {
  requireArgs(customerId, idToken);
  try {
    await client.delete(`/${customerId}/tiktok/connection`, {
      params: { profile_id: profileId },
      headers: { Authorization: `Bearer ${idToken}` },
    });
  } catch (error: unknown) {
    // Already gone on the server: the goal (disconnected) is met, so succeed.
    if (isProfileNotConnected(error)) return;
    debug.error("TikTok disconnect failed:", formatSafeErrorMessage(error));
    throw new Error(tiktokErrorMessage(error, "Failed to disconnect TikTok"));
  }
}
